import { camelCase } from 'lodash';

import { MetricCounts, MetricCountsTimeSeriesValues } from '@occc/graphql';
import {
  calcDateTime,
  DateTimeFormat,
  DateTimeFormatTZ,
  diffDateTime,
  DurationUnit,
  formatDateTime,
  formatDateTimeTZ,
  getUserTimezone,
  isBeforeDateTime,
  isSameDateTime,
  parseDate,
  toLocalDateTime,
} from '@occc-app/lib/date';
import { RangeTypeWithCustom } from '@occc/ui-design-system';

import { Metric, MetricDataPoint, Metrics, MetricType } from './types';

export const defaultMetricValues = (): Metric => ({
  interval: 'day',
  dataPoints: [],
  subtotal: 0,
  priorSubtotal: 0,
  total: 0,
});

const findOrCreateDataPoint = (dataPoints: MetricDataPoint[], date: Date) => {
  const existingDataPoint = dataPoints.find((dataPoint) =>
    // TODO what precession to use ???
    isSameDateTime(dataPoint.date, date, 'millisecond'),
  );
  if (existingDataPoint) {
    return existingDataPoint;
  }

  const newDataPoint: MetricDataPoint = {
    date,
    count: 0,
  };
  dataPoints.push(newDataPoint);
  return newDataPoint;
};

export const metricParser = (
  data: MetricCounts[],
  startDate: Date,
  endDate: Date,
  interval: DurationUnit,
): Metrics => {
  const metrics = {
    membershipCreated: defaultMetricValues(),
    offerFulfillment: defaultMetricValues(),
    pointsBurned: defaultMetricValues(),
    pointsEarned: defaultMetricValues(),
    redemptionCreated: defaultMetricValues(),
    revenue: defaultMetricValues(),
    transactionCreated: defaultMetricValues(),
  };

  const requestedRange = diffDateTime(endDate, startDate, interval) + 1;

  data.map((apiMetric: MetricCounts) => {
    const dataPoints: MetricDataPoint[] = [];
    apiMetric?.timeSeries.values.forEach((apiValue: MetricCountsTimeSeriesValues) => {
      let currentDate = parseDate(apiValue.date);
      if (isBeforeDateTime(currentDate, startDate)) {
        currentDate = calcDateTime(currentDate, 'add', requestedRange, interval);
        const dataPoint = findOrCreateDataPoint(dataPoints, currentDate);
        dataPoint.priorCount = parseInt(apiValue.count, 10); // value is passed as a string since it may be larger than 40bln
      } else {
        const dataPoint = findOrCreateDataPoint(dataPoints, currentDate);
        dataPoint.count = parseInt(apiValue.count, 10); // value is passed as a string since it may be larger than 40bln
      }
    });

    const subtotal = dataPoints.reduce((sum, dataPoint) => sum + dataPoint.count, 0);
    const priorSubtotal = dataPoints.reduce(
      (sum, dataPoint) => sum + (dataPoint.priorCount || 0),
      0,
    );

    const metric = {
      interval,
      dataPoints,
      subtotal,
      priorSubtotal,
      total: parseInt(apiMetric.total, 10), // value is passed as a string since it may be larger than 40bln
    };
    return ((metrics as any)[camelCase(apiMetric.eventType)] = metric);
  });

  return metrics;
};

export const getWelcomeMessage = () => {
  const currentHour = new Date().getHours();
  if (currentHour < 12) {
    return 'Good Morning, check in and see how your program is performing!';
  }
  if (currentHour < 17) {
    return 'Good Afternoon, check in and see how your program is performing!';
  }
  return 'Good Evening, check in and see how your program is performing!';
};

export const getSelectedMetricByType = (
  selectedMetricType: MetricType,
  metrics: Metrics,
): Metric => {
  const metricMap: Record<MetricType, Metric> = {
    offers: metrics.offerFulfillment,
    redemptions: metrics.redemptionCreated,
    transactions: metrics.transactionCreated,
    members: metrics.membershipCreated,
  };

  return metricMap[selectedMetricType];
};

export const formatChartTooltipTitle = (
  interval: DurationUnit,
  selectedMetricType: MetricType,
  utcTime: Date,
) => {
  const tooltipTitleMap: Record<MetricType, string> = {
    offers: 'Offers',
    redemptions: 'Redemptions',
    transactions: 'Loyalty Transactions',
    members: 'Members',
  };

  if (interval === 'hour') {
    const localTime = toLocalDateTime(utcTime);
    const tz = formatDateTimeTZ(utcTime, getUserTimezone(), DateTimeFormatTZ.ShortTimeZone);

    return `${formatDateTime(localTime, DateTimeFormat.Time12h)} ${tz}`;
  }

  return tooltipTitleMap[selectedMetricType] || '';
};

export const getIntervalFromRangeType = (rangeType: RangeTypeWithCustom): DurationUnit => {
  return ['today', 'yesterday'].includes(rangeType) ? 'hour' : 'day';
};
